import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, MapPin, Clock, Users, Shield, AlertTriangle, CheckCircle2,
  Camera, Send, Flag, X, ChevronRight, AlertCircle, Navigation,
  MoreVertical, Edit3, Trash2, HandCoins, LogOut, Maximize2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PhotoLightbox from "@/components/PhotoLightbox";
import MissionMgmtMenu from "@/components/MissionMgmtMenu";
import MissionEditSheet from "@/components/MissionEditSheet";
import NearbyHelpers from "@/components/NearbyHelpers";

const STATUS_META = {
  open: { label: "Aperta", color: "#FFB020" },
  in_progress: { label: "In corso", color: "#34D399" },
  pending_close: { label: "Verifica chiusura", color: "#FFB020" },
  closed: { label: "Chiusa", color: "#9ca3af" },
};

const OUTCOME_META = {
  saved: { label: "Cane salvato", color: "#34D399" },
  transferred: { label: "Trasferito", color: "#FFB020" },
  not_resolved: { label: "Non risolta", color: "#FF3B30" },
};

const PROGRESSION = [
  { id: "reported",  label: "Segnalata" },
  { id: "verifying", label: "In verifica" },
  { id: "incoming",  label: "Aiuto in arrivo" },
  { id: "on_site",   label: "Presa in carico" },
  { id: "safe",      label: "Cane al sicuro" },
];

function getProgressionStep(m) {
  if (m.status === "closed" || m.status === "pending_close") return 4;
  const onSite = (m.checkpoints || []).some((c) => c.type === "on_site");
  if (m.status === "in_progress" && onSite) return 3;
  if (m.status === "in_progress") return 2;
  if ((m.participants || []).length > 0) return 2;
  if ((m.checkpoints || []).length > 0) return 1;
  return 0;
}

export default function MissionDetail() {
  const { missionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [showCp, setShowCp] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/missions/v2/${missionId}`);
      setM(data);
      setLoading(false);
    } catch {
      toast.error("Missione non trovata");
      navigate("/app/missioni");
    }
  }, [missionId, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/missions/v2/${missionId}`);
        if (!cancelled) {
          setM(data);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          toast.error("Missione non trovata");
          navigate("/app/missioni");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [missionId, navigate]);

  if (loading || !m) {
    return <div className="max-w-3xl mx-auto p-6 text-[#6B6B6B] text-sm">Caricamento...</div>;
  }

  const isCreator = m.creator_id === user?.id;
  const isParticipant = (m.participants || []).some((p) => p.user_id === user?.id);
  const status = STATUS_META[m.status] || STATUS_META.open;
  const ageLabel = new Date(m.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  const requiredCloseConfirms = m.close_required || 0;
  const nonCreatorConfirms = (m.close_confirmations || []).filter(c => c.user_id !== m.creator_id);
  const hasConfirmed = nonCreatorConfirms.some(c => c.user_id === user?.id);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 pb-32">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate("/app/missioni")} data-testid="back-missions"
          className="text-xs text-[#9A9A9A] hover:text-[#1A1A1A] inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Tutte le missioni
        </button>
        {(isCreator || isParticipant) && (
          <MissionMgmtMenu
            mission={m}
            isCreator={isCreator}
            isParticipant={isParticipant}
            onEdit={() => setShowEdit(true)}
            onReload={load}
            onCancelled={() => navigate("/app/missioni")}
          />
        )}
      </div>

      {/* Header */}
      <div className="rounded-3xl border bg-white p-5 sm:p-6 relative overflow-hidden"
        style={{ borderColor: `${m.color}40` }}>
        <div aria-hidden className="absolute -top-px left-0 right-0 h-[2px]"
          style={{ background: m.color, boxShadow: `0 0 12px ${m.color}` }} />

        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[11px] text-[#9A9A9A]">{m.code}</span>
          <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
            style={{ background: `${status.color}20`, color: status.color }}>
            {status.label}
          </span>
          {m.urgency === "critical" && (
            <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
              style={{ background: "#FF3B3020", color: "#FF3B30" }}>
              Critica
            </span>
          )}
          <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
            style={{ background: `${m.color}20`, color: m.color }}>
            {m.category_label}
          </span>
        </div>

        <h1 className="font-display text-2xl sm:text-3xl tracking-tight mt-3">{m.title}</h1>
        <p className="text-[#6B6B6B] mt-2 text-sm leading-relaxed whitespace-pre-wrap">{m.description}</p>

        {m.photo_url && (
          <button
            type="button"
            onClick={() => setShowLightbox(true)}
            data-testid="mission-photo-btn"
            className="mt-4 relative w-full block group"
            aria-label="Ingrandisci foto"
          >
            <img src={m.photo_url} alt="" className="rounded-2xl max-h-72 w-full object-cover" />
            <span className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white opacity-90 group-active:scale-95 transition-transform">
              <Maximize2 className="h-4 w-4" />
            </span>
          </button>
        )}

        {/* Fundraise banner (Pass-through PayPal.me — ORIO never touches money) */}
        {m.fundraise_url && m.status !== "closed" && m.status !== "cancelled" && (
          <a
            href={m.fundraise_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mission-fundraise"
            className="mt-4 flex items-center gap-3 rounded-2xl border p-3 hover:brightness-105 transition"
            style={{
              background: "linear-gradient(135deg, rgba(255,122,26,0.10), rgba(244,184,60,0.10))",
              borderColor: "rgba(255,122,26,0.35)",
            }}
          >
            <div className="h-11 w-11 rounded-2xl flex items-center justify-center text-white shrink-0"
              style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                       boxShadow: "0 8px 22px -8px rgba(255,122,26,0.55)" }}>
              <HandCoins className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#C25612]">Raccolta fondi</div>
              <div className="text-sm font-semibold text-[#1A1A1A]">Sostieni questa missione su PayPal</div>
              <div className="text-[11px] text-[#7A7A7A] mt-0.5">
                ORIO verifica il creatore, ma la donazione va direttamente a lui su PayPal.me.
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-[#C25612] shrink-0" />
          </a>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[#9A9A9A]">
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" />
            {m.city || "—"} {m.address_hint && `· ${m.address_hint}`}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" /> Aperta {ageLabel}
          </span>
          <span className="inline-flex items-center gap-1.5">
            {m.creator_role === "shelter" ? "🏠" : m.creator_role === "activist" ? "✊" : "👤"}
            {m.creator_shelter_name || m.creator_name}
          </span>
        </div>

        {/* Outcome banner */}
        {m.outcome && (
          <div className="mt-4 rounded-2xl border p-3 flex items-center gap-3"
            style={{
              borderColor: `${OUTCOME_META[m.outcome]?.color}50`,
              background: `${OUTCOME_META[m.outcome]?.color}10`,
            }}>
            <CheckCircle2 className="h-5 w-5" style={{ color: OUTCOME_META[m.outcome]?.color }} />
            <div>
              <div className="text-sm font-semibold" style={{ color: OUTCOME_META[m.outcome]?.color }}>
                Esito: {OUTCOME_META[m.outcome]?.label}
              </div>
              <div className="text-xs text-[#9A9A9A]">Confermato da {(m.close_confirmations || []).length} persone</div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-5 flex flex-wrap gap-2" data-testid="mission-actions">
          {!isCreator && !isParticipant && m.status !== "closed" && (
            <button data-testid="mission-join" onClick={() => setShowJoin(true)}
              className="orio-cta-btn relative px-4 py-2.5 rounded-full font-semibold text-black inline-flex items-center gap-2">
              <Users className="h-4 w-4" /> Mi rendo disponibile
            </button>
          )}
          {(isCreator || isParticipant) && m.status !== "closed" && (
            <button data-testid="mission-update" onClick={() => setShowCp(true)}
              className="px-4 py-2.5 rounded-full bg-white/8 hover:bg-white/12 text-sm font-medium inline-flex items-center gap-2">
              <Camera className="h-4 w-4" /> Aggiorna stato
            </button>
          )}
          {isCreator && m.status !== "closed" && m.status !== "pending_close" && (
            <button data-testid="mission-request-close" onClick={() => setShowClose(true)}
              className="px-4 py-2.5 rounded-full bg-white/8 hover:bg-white/12 text-sm font-medium inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> Chiudi missione
            </button>
          )}
          {m.status === "pending_close" && isParticipant && !hasConfirmed && (
            <button data-testid="mission-confirm-close" onClick={() => setShowClose(true)}
              style={{ background: "#34D399", color: "#000" }}
              className="px-4 py-2.5 rounded-full font-semibold text-sm inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> Conferma l&apos;esito
            </button>
          )}
          {(isParticipant || isCreator) && m.status === "pending_close" && (
            <DisputeButton missionId={m.id} reload={load} />
          )}
        </div>

        {m.status === "pending_close" && requiredCloseConfirms > 0 && (
          <div className="mt-3 text-xs text-[#9A9A9A] inline-flex items-center gap-2 rounded-lg bg-[#FAF7F0] border border-[#F0EBE3] px-3 py-2">
            <AlertTriangle className="h-3.5 w-3.5 text-[#FFB020]" />
            Chiusura richiesta: servono ancora {Math.max(0, requiredCloseConfirms - nonCreatorConfirms.length)} conferme
            dai partecipanti per finalizzare e accreditare il karma.
          </div>
        )}
      </div>

      {/* Status progression */}
      <Section title="Stato missione" testid="status-progression">
        <ProgressionBar step={getProgressionStep(m)} accent={m.color} />
      </Section>

      {/* Safety box for critical missions */}
      {m.urgency === "critical" && m.status !== "closed" && (
        <div className="mt-5 rounded-2xl border p-4 flex items-start gap-3"
          style={{ background: "rgba(255,59,48,0.08)", borderColor: "rgba(255,59,48,0.35)" }}
          data-testid="safety-box">
          <AlertCircle className="h-5 w-5 text-[#FF3B30] shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-[#FF3B30]">Avviso sicurezza</div>
            <p className="text-[12px] text-[#1A1A1A] mt-1 leading-relaxed">
              Non avvicinarti se il cane è spaventato o aggressivo. Mantieni distanza, aggiorna la posizione e contatta un rifugio o un professionista.
            </p>
          </div>
        </div>
      )}

      {/* Quick action shortcuts for participants/creator */}
      {(isParticipant || isCreator) && m.status !== "closed" && (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="quick-actions">
          <QuickAction icon={Users} label="Sono sul posto" testid="qa-on-site"
            onClick={() => setShowCp(true)} accent="#34D399" />
          <QuickAction icon={Navigation} label="Aggiorna posizione" testid="qa-update-loc"
            onClick={() => setShowCp(true)} accent="#FFB020" />
          <QuickAction icon={Camera} label="Aggiungi aggiornamento" testid="qa-add-update"
            onClick={() => setShowCp(true)} accent="#FFB020" />
          {isCreator && (
            <>
              <QuickAction icon={X} label="Cane non più visibile" testid="qa-not-visible"
                onClick={() => setShowCp(true)} accent="#FF3B30" />
              <QuickAction icon={CheckCircle2} label="Cane al sicuro" testid="qa-safe"
                onClick={() => setShowClose(true)} accent="#34D399" hero />
            </>
          )}
        </div>
      )}

      {/* Participants */}
      <Section title={`Squadra (${(m.participants || []).length})`} testid="participants-section">
        {(m.participants || []).length === 0 ? (
          <div className="text-sm text-[#9A9A9A]">Nessun partecipante ancora.</div>
        ) : (
          <div className="space-y-2">
            {(m.participants || []).map((p) => (
              <Link
                key={p.user_id}
                to={`/app/u/${p.user_id}`}
                data-testid={`participant-${p.user_id}`}
                className="flex items-center gap-3 rounded-xl bg-white/[0.025] border border-[#F0EBE3] px-3 py-2.5 hover:bg-[#FAF7F0] active:scale-[0.99] transition"
              >
                <div className="h-8 w-8 rounded-full bg-[#F0EBE3] flex items-center justify-center text-xs">
                  {p.name?.[0] || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{p.name}</div>
                  <div className="text-[11px] text-[#9A9A9A]">
                    {p.role === "shelter" ? "Rifugio" : p.role === "activist" ? "Attivista" : "Cittadino"}
                    {p.skills?.length > 0 && ` · ${p.skills.join(", ")}`}
                  </div>
                </div>
                <span className="text-[10px] text-[#9A9A9A]">{relTime(p.joined_at)}</span>
              </Link>
            ))}
          </div>
        )}
      </Section>

      {/* Nearby helpers — opt-in users within 15 km with their first dog.
          Only surfaced while the mission is open/in-progress to avoid
          leaking presence data after a closure. */}
      {["open", "in_progress"].includes(m.status) && (
        <NearbyHelpers missionId={m.id} />
      )}

      {/* Timeline checkpoints */}
      <Section title="Timeline operativa" testid="timeline-section">
        {(m.checkpoints || []).length === 0 ? (
          <div className="text-sm text-[#9A9A9A]">Nessun aggiornamento ancora. Quando uno della squadra arriva sul posto o avvista qualcosa, comparirà qui.</div>
        ) : (
          <div className="relative pl-6">
            <div aria-hidden className="absolute left-2 top-2 bottom-2 w-px bg-[#F0EBE3]" />
            <div className="space-y-4">
              {(m.checkpoints || []).slice().reverse().map((cp) => (
                <div key={cp.id} className="relative">
                  <div aria-hidden className="absolute -left-4 top-1.5 h-2.5 w-2.5 rounded-full"
                    style={{ background: m.color, boxShadow: `0 0 8px ${m.color}` }} />
                  <div className="text-[10px] uppercase tracking-widest text-[#9A9A9A]">
                    {cp.type === "sighting" ? "Avvistamento" : cp.type === "on_site" ? "Sul posto" : "Aggiornamento"}
                    {" · "}{new Date(cp.created_at).toLocaleString("it-IT")}
                  </div>
                  <div className="text-sm mt-0.5">
                    <span className="font-medium">{cp.name}</span>: {cp.note}
                  </div>
                  {cp.photo_url && (
                    <img src={cp.photo_url} alt="" className="mt-2 rounded-xl max-h-44 object-cover" />
                  )}
                  {cp.lat && cp.lng && (
                    <div className="font-mono text-[10px] text-[#9A9A9A] mt-1">
                      {cp.lat.toFixed(4)}, {cp.lng.toFixed(4)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Section>

      {/* Confirmations */}
      {(m.close_confirmations || []).length > 0 && (
        <Section title="Conferme di chiusura" testid="confirmations-section">
          <div className="space-y-2">
            {m.close_confirmations.map((c) => (
              <div key={c.user_id} className="flex items-center gap-3 rounded-xl bg-[#34D399]/10 border border-[#34D399]/30 px-3 py-2.5">
                <CheckCircle2 className="h-4 w-4 text-[#34D399]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{c.name}</div>
                  <div className="text-[11px] text-[#9A9A9A]">
                    {OUTCOME_META[c.outcome]?.label} {c.note && `· ${c.note}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {showJoin && <JoinModal mission={m} onClose={() => setShowJoin(false)} onDone={() => { setShowJoin(false); load(); }} />}
      {showCp && <CheckpointModal mission={m} onClose={() => setShowCp(false)} onDone={() => { setShowCp(false); load(); }} />}
      {showClose && (
        <CloseModal mission={m} onClose={() => setShowClose(false)} onDone={() => { setShowClose(false); load(); }}
          mode={isCreator && m.status !== "pending_close" ? "request" : "confirm"} />
      )}
      {showEdit && (
        <MissionEditSheet
          mission={m}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); load(); }}
        />
      )}
      {showLightbox && m.photo_url && (
        <PhotoLightbox src={m.photo_url} alt={m.title} onClose={() => setShowLightbox(false)} />
      )}
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="mt-8" data-testid={testid}>
      <h3 className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] mb-3">{title}</h3>
      {children}
    </div>
  );
}

function ProgressionBar({ step, accent = "#FFB020" }) {
  return (
    <div>
      <div className="flex items-center gap-1.5">
        {PROGRESSION.map((s, i) => {
          const reached = i <= step;
          return (
            <div key={s.id} className="flex-1">
              <div
                className="h-1.5 rounded-full transition-all"
                style={{
                  background: reached ? accent : "rgba(255,255,255,0.08)",
                  boxShadow: i === step ? `0 0 10px ${accent}` : "none",
                }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-2.5 grid grid-cols-5 gap-1 text-center">
        {PROGRESSION.map((s, i) => {
          const reached = i <= step;
          return (
            <div key={s.id} className="text-[10px] leading-tight px-0.5"
              style={{ color: reached ? (i === step ? accent : "rgba(255,255,255,0.85)") : "rgba(255,255,255,0.35)" }}>
              {s.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, accent = "#fff", testid, hero = false }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="rounded-xl p-3 text-left transition-all flex items-center gap-2.5 border"
      style={{
        background: hero ? `${accent}1F` : "rgba(255,255,255,0.03)",
        borderColor: hero ? `${accent}55` : "rgba(255,255,255,0.08)",
      }}
    >
      <div
        className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: `${accent}22`, color: accent, border: `1px solid ${accent}33` }}
      >
        <Icon className="h-3.5 w-3.5" />
      </div>
      <span className="text-[12px] font-medium leading-tight">{label}</span>
    </button>
  );
}

function relTime(iso) {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.floor(m/60)}h`;
  return `${Math.floor(m/1440)}g`;
}

function JoinModal({ mission, onClose, onDone }) {
  const [skills, setSkills] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const ALL = ["auto", "posto in casa", "tempo libero", "esperienza vet", "esperienza recupero", "trasporto attrezzato"];
  const toggle = (s) => setSkills((sl) => sl.includes(s) ? sl.filter((x) => x !== s) : [...sl, s]);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/missions/v2/${mission.id}/join`, { skills, note });
      toast.success("Ti sei unito alla missione");
      onDone();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  return (
    <Modal onClose={onClose} title="Mi rendo disponibile" testid="join-modal">
      <p className="text-xs text-[#9A9A9A]">Aiuti che puoi offrire:</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {ALL.map((s) => (
          <button key={s} type="button" onClick={() => toggle(s)}
            data-testid={`skill-${s.replace(/ /g,"-")}`}
            className="px-3 py-1.5 rounded-full text-xs border transition-all"
            style={{
              background: skills.includes(s) ? "rgba(52,211,153,0.15)" : "rgba(255,255,255,0.03)",
              borderColor: skills.includes(s) ? "rgba(52,211,153,0.45)" : "rgba(255,255,255,0.1)",
              color: skills.includes(s) ? "#34D399" : "rgba(255,255,255,0.7)",
            }}>
            {s}
          </button>
        ))}
      </div>
      <textarea
        data-testid="join-note"
        placeholder="Note opzionali (orari disponibili, contatti...)"
        value={note} onChange={(e) => setNote(e.target.value)}
        className="mt-4 w-full px-3 py-2.5 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-white/30 min-h-[70px] text-sm"
      />
      <button data-testid="join-submit" onClick={submit} disabled={loading}
        className="mt-4 orio-cta-btn relative w-full px-4 py-2.5 rounded-full font-semibold text-black inline-flex items-center justify-center gap-2 disabled:opacity-50">
        {loading ? "..." : <><Users className="h-4 w-4" /> Mi unisco</>}
      </button>
    </Modal>
  );
}

function CheckpointModal({ mission, onClose, onDone }) {
  const [type, setType] = useState("update");
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState(null);
  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(false);

  const onPhotoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Foto troppo grande (max 2MB)");
    const r = new FileReader();
    r.onload = () => setPhoto(r.result);
    r.readAsDataURL(f);
  };
  const geo = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => toast.error("GPS non disponibile")
    );
  };

  const submit = async () => {
    if (!note.trim()) return toast.error("Aggiungi una nota");
    setLoading(true);
    try {
      await api.post(`/missions/v2/${mission.id}/checkpoint`, {
        type, note, photo_url: photo, lat: coords?.lat, lng: coords?.lng,
      });
      toast.success("Aggiornamento pubblicato");
      onDone();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Modal onClose={onClose} title="Aggiorna lo stato" testid="checkpoint-modal">
      <div className="grid grid-cols-3 gap-2">
        {[
          { id: "sighting", label: "Avvistamento" },
          { id: "on_site", label: "Sul posto" },
          { id: "update", label: "Note" },
        ].map((t) => (
          <button key={t.id} type="button" onClick={() => setType(t.id)}
            data-testid={`cp-type-${t.id}`}
            className="px-2 py-2 rounded-xl text-xs border transition-all"
            style={{
              background: type === t.id ? "rgba(255,176,32,0.15)" : "rgba(255,255,255,0.03)",
              borderColor: type === t.id ? "rgba(255,176,32,0.45)" : "rgba(255,255,255,0.1)",
              color: type === t.id ? "#FFB020" : "rgba(255,255,255,0.7)",
            }}>
            {t.label}
          </button>
        ))}
      </div>
      <textarea data-testid="cp-note" value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Descrivi cosa hai visto / fatto"
        className="mt-3 w-full px-3 py-2.5 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-white/30 min-h-[80px] text-sm" />
      <div className="mt-3 flex gap-2 items-center flex-wrap">
        <label className="px-3 py-2 rounded-xl bg-[#F4EFE6] hover:bg-[#F0EBE3] text-xs inline-flex items-center gap-1.5 cursor-pointer">
          <Camera className="h-3.5 w-3.5" /> {photo ? "Cambia foto" : "Aggiungi foto"}
          <input data-testid="cp-photo" type="file" accept="image/*" onChange={onPhotoChange} className="hidden" />
        </label>
        <button type="button" onClick={geo} data-testid="cp-geo"
          className="px-3 py-2 rounded-xl bg-[#F4EFE6] hover:bg-[#F0EBE3] text-xs inline-flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5" /> {coords ? "GPS ✓" : "Allega GPS"}
        </button>
        {photo && (<span className="text-[11px] text-[#9A9A9A]">Foto pronta</span>)}
      </div>
      <p className="text-[11px] text-[#9A9A9A] mt-2">
        Aggiungendo una foto + GPS guadagni +10 punti karma in attesa.
      </p>
      <button onClick={submit} disabled={loading} data-testid="cp-submit"
        className="mt-4 orio-cta-btn relative w-full px-4 py-2.5 rounded-full font-semibold text-black inline-flex items-center justify-center gap-2 disabled:opacity-50">
        {loading ? "..." : <><Send className="h-4 w-4" /> Pubblica aggiornamento</>}
      </button>
    </Modal>
  );
}

function CloseModal({ mission, onClose, onDone, mode }) {
  const [outcome, setOutcome] = useState("saved");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      const url = mode === "request"
        ? `/missions/v2/${mission.id}/request-close`
        : `/missions/v2/${mission.id}/confirm-close`;
      await api.post(url, { outcome, note });
      toast.success(mode === "request" ? "Richiesta inviata ai partecipanti" : "Conferma registrata");
      onDone();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  return (
    <Modal onClose={onClose}
      title={mode === "request" ? "Chiudi missione" : "Conferma esito"}
      testid="close-modal">
      <p className="text-xs text-[#9A9A9A]">
        {mode === "request"
          ? "Dichiara l'esito reale. I partecipanti dovranno confermare prima dell'accredito karma."
          : "Conferma che la missione è stata davvero risolta come dichiarato dal creatore."}
      </p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {Object.entries(OUTCOME_META).map(([id, meta]) => (
          <button key={id} type="button" onClick={() => setOutcome(id)}
            data-testid={`outcome-${id}`}
            className="px-2 py-2 rounded-xl text-xs border transition-all"
            style={{
              background: outcome === id ? `${meta.color}1a` : "rgba(255,255,255,0.03)",
              borderColor: outcome === id ? `${meta.color}50` : "rgba(255,255,255,0.1)",
              color: outcome === id ? meta.color : "rgba(255,255,255,0.7)",
            }}>
            {meta.label}
          </button>
        ))}
      </div>
      <textarea data-testid="close-note" value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Dettagli su come si è risolta (opzionale)"
        className="mt-3 w-full px-3 py-2.5 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-white/30 min-h-[60px] text-sm" />
      <button data-testid="close-submit" onClick={submit} disabled={loading}
        className="mt-4 orio-cta-btn relative w-full px-4 py-2.5 rounded-full font-semibold text-black inline-flex items-center justify-center gap-2 disabled:opacity-50">
        {loading ? "..." : <><CheckCircle2 className="h-4 w-4" /> {mode === "request" ? "Richiedi chiusura" : "Conferma"}</>}
      </button>
    </Modal>
  );
}

function DisputeButton({ missionId, reload }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const submit = async () => {
    if (!reason.trim()) return toast.error("Indica una ragione");
    try {
      await api.post(`/missions/v2/${missionId}/dispute`, { reason });
      toast.success("Dispute registrata. Karma congelato in attesa di revisione.");
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };
  return (
    <>
      <button data-testid="dispute-btn" onClick={() => setOpen(true)}
        className="px-4 py-2.5 rounded-full bg-[#F4EFE6] hover:bg-[#F0EBE3] text-sm text-[#FF3B30] inline-flex items-center gap-2">
        <Flag className="h-4 w-4" /> Apri dispute
      </button>
      {open && (
        <Modal onClose={() => setOpen(false)} title="Apri una dispute" testid="dispute-modal">
          <p className="text-xs text-[#9A9A9A]">
            Se ritieni che la chiusura non sia veritiera, apri una dispute. Il karma viene congelato e verrà revisionato.
          </p>
          <textarea data-testid="dispute-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Spiega perché contesti la chiusura"
            className="mt-3 w-full px-3 py-2.5 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-white/30 min-h-[80px] text-sm" />
          <button data-testid="dispute-submit" onClick={submit}
            className="mt-4 w-full px-4 py-2.5 rounded-full bg-[#FF3B30] text-[#1A1A1A] font-semibold text-sm inline-flex items-center justify-center gap-2">
            <Flag className="h-4 w-4" /> Conferma dispute
          </button>
        </Modal>
      )}
    </>
  );
}

function Modal({ onClose, title, children, testid }) {
  return (
    <div className="fixed inset-0 z-[1200] bg-black/80 backdrop-blur-sm overflow-y-auto" data-testid={testid}>
      <div className="min-h-full flex items-start sm:items-center justify-center p-4 py-8 pt-20 sm:pt-8 pr-4 sm:pr-24">
        <div className="w-full max-w-md bg-white border border-[#F0EBE3] rounded-3xl p-5 relative">
          <button onClick={onClose} className="absolute right-3 top-3 h-8 w-8 rounded-full bg-[#F4EFE6] hover:bg-[#F0EBE3] flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
          <h3 className="font-display text-xl tracking-tight">{title}</h3>
          <div className="mt-3">{children}</div>
        </div>
      </div>
    </div>
  );
}
